/**
 * Application Constants
 * Centralized constants for the application
 */

// API
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
export const API_TIMEOUT = 30000;

// Partner / Tenant fijo: UNE-Credito es el unico portafolio operado. Fiado SA es
// el operador (cambia la vista del back-office), no un partner separado con su
// propia data. Override solo para entornos de prueba donde se simulen otros tenants.
export const PARTNER_ID = import.meta.env.VITE_PARTNER_ID || 'UNE';

// Pagination
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  THEME: 'theme',
} as const;

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  REGISTER: '/register',
  DASHBOARD: '/dashboard',
  USUARIOS: '/usuarios',
  PROFILE: '/profile',
  SETTINGS: '/settings',

  // Credit
  CREDIT_DASHBOARD: '/credit/dashboard',
  PIPELINE_MATRIX: '/credit/pipeline',
  ACTIVE_CREDITS: '/credit/active',
  CREDIT_DETAIL: '/credit/detail/:creditId',
  NURTURING: '/credit/nurturing',
  NURTURING_DETAIL: '/credit/nurturing/:userId',
  ORIGINATION: '/credit/origination',
  SCORING: '/credit/scoring',

  // Credit CRUD
  APPLICATIONS_LIST: '/credit/applications',
  APPLICATION_DETAIL: '/credit/applications/:applicationId',
  OFFERS_LIST: '/credit/offers',
  OFFER_DETAIL: '/credit/offers/:offerId',
  FINANCIAL_PROFILE: '/credit/profiles/:directoryId',
  CREDIT_CANDIDATOS: '/credit/candidatos',

  // Clientes (P10)
  CLIENTS_LIST: '/backoffice/clientes',
  EXPEDIENTES_SEARCH: '/backoffice/expedientes',
  EXPEDIENTE_DETAIL: '/backoffice/expedientes/:directoryId',

  // Collection
  COLLECTION_DASHBOARD: '/collection/dashboard',
  COLLECTION_AGENDADOS: '/collection/agendados',
  COLLECTION_CASTIGADOS: '/collection/castigados',
  COLLECTION_COBRADOS: '/collection/cobrados',
  COLLECTION_CARTERA: '/collection/cartera',
  COLLECTION_DAY: '/collection/day',
  DELINQUENCY: '/collection/delinquency',
  LATENT_COLLECTION: '/collection/latent',
  LIENS_LIST: '/collection/liens',
  LIEN_DETAIL: '/collection/liens/:lienId',
  COLLECTION_TRANSFERS: '/collection/transfers',

  // Portfolio
  PORTFOLIO_DASHBOARD: '/portfolio/dashboard',
  PORTFOLIO_HISTORICO: '/portfolio/historico',
  PORTFOLIO_ACTIVE: '/portfolio/active',
  PORTFOLIO_DELINQUENT: '/portfolio/delinquent',
  PORTFOLIO_DEFAULTED: '/portfolio/defaulted',

  // Funding
  FONDEO: '/funding/fondeo',
  CONFIRMAR_FONDEO: '/funding/confirmar',
  FUNDING_INSTRUCTIONS: '/funding/instructions',
  FUNDING_INSTRUCTION_DETAIL: '/funding/instructions/:fundingId',
  FUNDING_HISTORY: '/funding/history',
  TRANSFER_HISTORY: '/funding/transfers',
  TRANSFER_DETAIL: '/funding/transfers/:ref',
  TRANSFERENCIAS: '/funding/transferencias',
  CONFIRMAR_TRANSFERENCIA: '/funding/confirmar-transferencia',
  CIERRE_MENSUAL: '/funding/cierre-mensual',

  // Policies & Tools
  POLICIES: '/policies',
  CONFIG_HISTORY: '/config/history',
  CREDIT_SIMULATOR: '/tools/simulator',

  // Operations (FiadoSA)
  OPS_DEFINITIONS: '/ops/definitions',
  RECONCILIATION_DASHBOARD: '/ops/reconciliation',
  RECONCILIATION_COLLECTOR: '/ops/reconciliation/collector',
  RECONCILIATION_FSA: '/ops/reconciliation/fsa',
  RECONCILIATION_PCF: '/ops/reconciliation/pcf',
  RECONCILIATION_DISCREPANCIES: '/ops/reconciliation/discrepancies',
  JOBS_CALENDAR: '/ops/jobs',
  JOBS_DETAIL: '/ops/jobs/:jobName',
  ALERTS: '/ops/alerts',
  SPLIT_CONFIG: '/ops/split',

  // Operations (UNE-Credito)
  SIC_REPORT: '/ops/une-credito/reporte-sic',
  SIC_REPORT_DETAIL: '/ops/une-credito/reporte-sic/:reportId',
} as const;

// Date formats
export const DATE_FORMATS = {
  SHORT: { year: 'numeric', month: 'short', day: 'numeric' } as const,
  LONG: { year: 'numeric', month: 'long', day: 'numeric' } as const,
  WITH_TIME: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  } as const,
} as const;

// Breakpoints (matching CSS tokens)
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
} as const;

// ─── Permissions ────────────────────────────────────────────────────────────
// Pattern: {Module}.{Action} — matches JWT permissions from Cognito

// ─── Permisos RBAC del tenant `une` (fuente única) ──────────────────────────
// Strings reales evaluados por @RequirePermission en backend. Consumidos por el
// sidebar (src/constants) y por <Can>/useCan en los botones de acción.
export const UNE_PERM = {
  APP_ACCESS: 'une.app.access',
  CLIENT_VIEW: 'une.client.view',
  ORIGINATION_VIEW: 'une.origination.view',
  PORTFOLIO_VIEW: 'une.portfolio.view',
  COLLECTION_VIEW: 'une.collection.view',
  LIEN_VIEW: 'une.lien.view',
  POLICY_VIEW: 'une.policy.view',
  POLICY_EDIT: 'une.policy.edit',
  SIMULATOR_USE: 'une.simulator.use',
  EXPEDIENTE_VIEW: 'une.expediente.view',
  EXPEDIENTE_AUDIT: 'une.expediente.audit.view',
  FUNDING_EXECUTE: 'une.funding.execute',
  FUNDING_CONFIRM: 'une.funding.confirm',
  TRANSFER_EXECUTE: 'une.transfer.execute',
  TRANSFER_CONFIRM: 'une.transfer.confirm',
  RECONCILIATION_VIEW: 'une.reconciliation.view',
  RECONCILIATION_RUN: 'une.reconciliation.run',
  SIC_VIEW: 'une.sic.view',
  SIC_RUN: 'une.sic.run',
  DEFINITIONS_MANAGE: 'une.definitions.manage',
  ALERT_VIEW: 'une.alert.view',
  ALERT_ACK: 'une.alert.ack',
  JOB_VIEW: 'une.job.view',
  JOB_RUN: 'une.job.run',
  PAYMENT_APPLY: 'une.payment.apply',
} as const;

export type UnePermission = (typeof UNE_PERM)[keyof typeof UNE_PERM];

export const PERMISSIONS = {
  // Credit
  CREDIT_DASHBOARD: 'CreditDashboard.Page',
  ACTIVE_CREDITS: 'ActiveCredits.Page',
  CREDIT_DETAIL: 'CreditDetail.Page',
  NURTURING: 'Nurturing.Page',
  ORIGINATION: 'Origination.Page',
  SCORING: 'Scoring.Page',

  // Collection
  COLLECTION_DASHBOARD: 'CollectionDashboard.Page',
  COLLECTION_DAY: 'CollectionDay.Page',
  DELINQUENCY: 'Delinquency.Page',
  LATENT_COLLECTION: 'LatentCollection.Page',
  COLLECTION_TRANSFERS: 'CollectionTransfers.Page',

  // Portfolio
  PORTFOLIO_DASHBOARD: 'PortfolioDashboard.Page',
  PORTFOLIO_ACTIVE: 'PortfolioActive.Page',
  PORTFOLIO_DELINQUENT: 'PortfolioDelinquent.Page',
  PORTFOLIO_DEFAULTED: 'PortfolioDefaulted.Page',

  // Funding
  FUNDING_INSTRUCTIONS: 'FundingInstructions.Page',
  FUNDING_HISTORY: 'FundingHistory.Page',
  TRANSFER_HISTORY: 'TransferHistory.Page',

  // Policies
  CREDIT_POLICIES: 'CreditPolicies.Page',
  // RBAC: permiso real del tenant une (formato une.*), reemplaza el viejo 'CreditPolicies.PUT'
  CREDIT_POLICIES_EDIT: UNE_PERM.POLICY_EDIT,
  CREDIT_SIMULATOR: 'CreditSimulator.Page',

  // Reports
  CREDIT_REPORTS: 'CreditReports.Page',

  // Early Payment
  EARLY_PAYMENT: 'EarlyPayment.POST',
} as const;

// ─── Sidebar Configuration ──────────────────────────────────────────────────

export interface SidebarChild {
  label: string;
  path: string;
  permission?: string;
}

export interface SidebarItem {
  label: string;
  icon: string;
  path?: string;
  permission?: string;
  children?: SidebarChild[];
}

export interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

/**
 * Sidebar config for FiadoSA admin role
 * Order follows the credit lifecycle:
 * 1. Clientes     — Expedientes, KYC, perfil del cliente
 * 2. Originacion  — Evaluar, puntuar, ofertar
 * 3. Portafolio   — Vista consolidada del portafolio
 * 4. Cobranza     — Gestion de cobros post-originacion
 * 5. Fondeo       — Financiamiento de operaciones
 * 6. Configuracion — Politicas, historial
 * 7. Operaciones  — Conciliacion, jobs, alertas
 */
export const FIADOSA_SIDEBAR: SidebarSection[] = [
  {
    title: 'Clientes',
    items: [
      {
        label: 'Listado',
        icon: 'Users',
        path: ROUTES.CLIENTS_LIST,
        permission: UNE_PERM.CLIENT_VIEW,
      },
    ],
  },
  {
    title: 'Originacion',
    items: [
      {
        label: 'Scoring',
        icon: 'Target',
        path: ROUTES.SCORING,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Pipeline',
        icon: 'BarChart3',
        path: ROUTES.PIPELINE_MATRIX,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Candidatos',
        icon: 'Users',
        path: ROUTES.CREDIT_CANDIDATOS,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Ofertas',
        icon: 'Tag',
        path: ROUTES.OFFERS_LIST,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Solicitudes',
        icon: 'FileText',
        path: ROUTES.APPLICATIONS_LIST,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
    ],
  },
  {
    title: 'Portafolio',
    items: [
      {
        label: 'Creditos',
        icon: 'CreditCard',
        path: ROUTES.ACTIVE_CREDITS,
        permission: UNE_PERM.PORTFOLIO_VIEW,
      },
      {
        label: 'Analisis',
        icon: 'PieChart',
        path: ROUTES.PORTFOLIO_DASHBOARD,
        permission: UNE_PERM.PORTFOLIO_VIEW,
      },
    ],
  },
  {
    title: 'Cobranza',
    items: [
      {
        label: 'Cobranza',
        icon: 'Receipt',
        path: ROUTES.COLLECTION_DASHBOARD,
        permission: UNE_PERM.COLLECTION_VIEW,
      },
      {
        label: 'Cobrados',
        icon: 'CheckCircle',
        path: ROUTES.COLLECTION_COBRADOS,
        permission: UNE_PERM.COLLECTION_VIEW,
      },
      {
        label: 'Castigados',
        icon: 'Skull',
        path: ROUTES.COLLECTION_CASTIGADOS,
        permission: UNE_PERM.COLLECTION_VIEW,
      },
    ],
  },
  {
    title: 'Fondeo',
    items: [
      {
        label: 'Confirmar Desembolsos',
        icon: 'CheckCircle',
        path: ROUTES.CONFIRMAR_FONDEO,
        permission: UNE_PERM.FUNDING_CONFIRM,
      },
      {
        label: 'Cobros',
        icon: 'Send',
        path: ROUTES.TRANSFERENCIAS,
        permission: UNE_PERM.TRANSFER_EXECUTE,
      },
      {
        label: 'Cierre Mensual',
        icon: 'BookOpen',
        path: ROUTES.CIERRE_MENSUAL,
        permission: UNE_PERM.TRANSFER_EXECUTE,
      },
    ],
  },
  {
    title: 'Configuracion',
    items: [
      {
        label: 'Politicas',
        icon: 'Shield',
        path: ROUTES.POLICIES,
        permission: UNE_PERM.POLICY_VIEW,
      },
      {
        label: 'Historial',
        icon: 'History',
        path: ROUTES.CONFIG_HISTORY,
        permission: UNE_PERM.POLICY_VIEW,
      },
    ],
  },
  {
    title: 'Operaciones',
    items: [
      {
        label: 'Definiciones',
        icon: 'Settings',
        path: ROUTES.OPS_DEFINITIONS,
        permission: UNE_PERM.DEFINITIONS_MANAGE,
      },
      {
        label: 'Conciliacion',
        icon: 'GitCompare',
        path: ROUTES.RECONCILIATION_DASHBOARD,
        permission: UNE_PERM.RECONCILIATION_VIEW,
      },
      {
        label: 'Alertas',
        icon: 'Bell',
        path: ROUTES.ALERTS,
        permission: UNE_PERM.ALERT_VIEW,
      },
    ],
  },
];

/**
 * Sidebar config for UNE-Credito partner role
 * Same lifecycle order as FiadoSA, fewer sections/items
 */
export const UNE_CREDITO_SIDEBAR: SidebarSection[] = [
  {
    title: 'Clientes',
    items: [
      {
        label: 'Listado',
        icon: 'Users',
        path: ROUTES.CLIENTS_LIST,
        permission: UNE_PERM.CLIENT_VIEW,
      },
    ],
  },
  {
    title: 'Originacion',
    items: [
      {
        label: 'Scoring',
        icon: 'Target',
        path: ROUTES.SCORING,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Pipeline',
        icon: 'BarChart3',
        path: ROUTES.PIPELINE_MATRIX,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Candidatos',
        icon: 'Users',
        path: ROUTES.CREDIT_CANDIDATOS,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Ofertas',
        icon: 'Tag',
        path: ROUTES.OFFERS_LIST,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
      {
        label: 'Solicitudes',
        icon: 'FileText',
        path: ROUTES.APPLICATIONS_LIST,
        permission: UNE_PERM.ORIGINATION_VIEW,
      },
    ],
  },
  {
    title: 'Portafolio',
    items: [
      {
        label: 'Creditos',
        icon: 'CreditCard',
        path: ROUTES.ACTIVE_CREDITS,
        permission: UNE_PERM.PORTFOLIO_VIEW,
      },
      {
        label: 'Analisis',
        icon: 'PieChart',
        path: ROUTES.PORTFOLIO_DASHBOARD,
        permission: UNE_PERM.PORTFOLIO_VIEW,
      },
    ],
  },
  {
    title: 'Cobranza',
    items: [
      {
        label: 'Cobranza',
        icon: 'Receipt',
        path: ROUTES.COLLECTION_DASHBOARD,
        permission: UNE_PERM.COLLECTION_VIEW,
      },
      {
        label: 'Cobrados',
        icon: 'CheckCircle',
        path: ROUTES.COLLECTION_COBRADOS,
        permission: UNE_PERM.COLLECTION_VIEW,
      },
      {
        label: 'Castigados',
        icon: 'Skull',
        path: ROUTES.COLLECTION_CASTIGADOS,
        permission: UNE_PERM.COLLECTION_VIEW,
      },
    ],
  },
  {
    title: 'Fondeo',
    items: [
      {
        label: 'Desembolsos',
        icon: 'Banknote',
        path: ROUTES.FONDEO,
        permission: UNE_PERM.FUNDING_EXECUTE,
      },
      {
        label: 'Confirmar Cobros',
        icon: 'CheckCircle',
        path: ROUTES.CONFIRMAR_TRANSFERENCIA,
        permission: UNE_PERM.TRANSFER_CONFIRM,
      },
      {
        label: 'Cierre Mensual',
        icon: 'BookOpen',
        path: ROUTES.CIERRE_MENSUAL,
        permission: UNE_PERM.TRANSFER_CONFIRM,
      },
    ],
  },
  {
    title: 'Configuracion',
    items: [
      {
        label: 'Politicas',
        icon: 'Shield',
        path: ROUTES.POLICIES,
        permission: UNE_PERM.POLICY_VIEW,
      },
      {
        label: 'Historial',
        icon: 'History',
        path: ROUTES.CONFIG_HISTORY,
        permission: UNE_PERM.POLICY_VIEW,
      },
    ],
  },
  {
    title: 'Operaciones',
    items: [
      {
        label: 'Reporte SIC',
        icon: 'FileBarChart',
        path: ROUTES.SIC_REPORT,
        permission: UNE_PERM.SIC_VIEW,
      },
    ],
  },
];
